import { useEffect, useMemo, useState } from "react";
import { Table, Spinner, Button, Input } from "reactstrap";
import { saveAs } from "file-saver";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { getPadInfoReport, exportPadInfoReportExcel, notifyPadAccounting, verifyPadEntry } from "../TenantForm/Helpers/PadInfo/api";
import { categoryMeta, money, nextMonthYm, formatMonthLabel, PAD_STATE_META, SOURCE_META } from "../TenantForm/Helpers/PadInfo/padInfoTypes";
import { resolveViewer } from "../../../../components/NotificationCenter/useNotificationFeed";

/**
 * "PAD" tab of the Master-Sheet Report modal. Previews the same two sheets accounting gets
 * in the Excel download:
 *   Sheet 1 — PAD updates (only units with PAD data), grouped by category, for a month
 *   Sheet 2 — every unit, colour-coded: green data / yellow document only / red nothing / grey vacant
 */
const PadInfoTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [month, setMonth] = useState(() => nextMonthYm());
  const [mode, setMode] = useState("month"); // "month" | "current"
  const [view, setView] = useState("updates"); // "updates" | "units"
  const [report, setReport] = useState(null);
  // Accounting's to-do view: only rows nobody has signed off yet. The Excel download
  // carries the same flag, so the screen and the workbook always agree.
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const effectiveMonth = mode === "month" ? month : null;

  useEffect(() => {
    let active = true;
    if (!mastersheetId) return undefined;
    setLoading(true);
    setError(null);
    getPadInfoReport(mastersheetId, effectiveMonth, unverifiedOnly)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load report");
        setReport(resp.data);
      })
      .catch((err) => active && setError(err?.response?.data?.message || err.message || "Failed to load PAD report"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, effectiveMonth, unverifiedOnly]);

  // "This month's PAD work is done" — the server emails accounting a completion notice
  // (company owner copied) with the month's Excel workbook attached, and posts the
  // in-app notification too.
  const [notifying, setNotifying] = useState(false);
  const handleNotifyAccounting = async () => {
    const confirmed = await Swal.fire({
      title: "Notify accounting?",
      html: `<div style="text-align:left;font-size:0.95rem;color:#334155;">Accounting will receive an email saying the PAD updates for <strong>${formatMonthLabel(month)}</strong> are all done, with the month's Excel report attached. The company owner is copied for visibility.</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, notify accounting",
      cancelButtonText: "Go back",
      confirmButtonColor: "#0C447C",
      reverseButtons: true,
    });
    if (!confirmed.isConfirmed) return;
    setNotifying(true);
    try {
      const resp = await notifyPadAccounting(mastersheetId, month);
      const n = resp?.data?.data?.recipients;
      toast.success(`Accounting notified${n ? ` · ${n} recipient${n === 1 ? "" : "s"}` : ""} — Excel report attached`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to notify accounting");
    } finally {
      setNotifying(false);
    }
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportPadInfoReportExcel(mastersheetId, effectiveMonth, unverifiedOnly);
      const name = `PAD-Info_${(masterSheetData?.building_name || "sheet").replace(/[^a-z0-9_-]+/gi, "_")}_${effectiveMonth || "current"}${unverifiedOnly ? "_unverified" : ""}.xlsx`;
      saveAs(new Blob([blob]), name);
    } catch (err) {
      console.error("PAD info excel export failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const t = report?.totals;
  const updateCount = useMemo(() => (report?.updates || []).reduce((s, g) => s + g.rows.length, 0), [report]);

  // Accounting verifies FROM this report — the checkbox moved here from the unit's PAD
  // table, because sign-off is a monthly, whole-property pass, not a per-unit visit.
  // Same capability rule as the notification centre: company/admin, and auditors with the
  // mastersheet privilege.
  const canVerify = !!useMemo(() => resolveViewer(), [])?.canVerify;
  const verifierName = useMemo(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem("authUser") || "{}");
      const u = parsed?.userData || parsed;
      return `${u?.firstname || ""} ${u?.lastname || ""}`.trim();
    } catch (_) {
      return "";
    }
  }, []);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const handleVerify = async (row, next) => {
    setVerifyBusy(row.pad_id);
    try {
      await verifyPadEntry(row.pad_id, { verified: next });
      setReport((r) => ({
        ...r,
        updates: (r.updates || []).map((g) => ({
          ...g,
          rows: g.rows.map((x) => (x.pad_id === row.pad_id ? { ...x, verified: next, verified_by: next ? verifierName || "you" : "" } : x)),
        })),
      }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update verification");
    } finally {
      setVerifyBusy(null);
    }
  };

  const rowClass = (u) => {
    if (u.occupancy === "VACANT") return "table-secondary";
    if (u.source === "DATA") return "table-success";
    if (u.doc_na) return "table-info";
    if (u.source === "DOCUMENT") return "table-warning";
    return "table-danger";
  };

  return (
    <div>
      <style>{`
        .pit-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 12px; }
        .pit-seg { display: inline-flex; border: 1px solid #D6DEEA; border-radius: 8px; overflow: hidden; }
        .pit-seg button { border: none; background: #fff; padding: 6px 12px; font-size: 12.5px; font-weight: 600; color: #33415C; cursor: pointer; font-family: inherit; }
        .pit-seg button.on { background: #0C447C; color: #fff; }
        .pit-seg button + button { border-left: 1px solid #D6DEEA; }
        .pit-cat { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 8px 8px 0 0; color: #fff; font-weight: 700; font-size: 13px; margin-top: 14px; }
        .pit-cat span.n { background: rgba(255,255,255,0.25); padding: 1px 8px; border-radius: 999px; font-size: 12px; }
        .pit-details { max-width: 380px; line-height: 1.35; }
        .pit-notes { max-width: 260px; white-space: pre-wrap; color: #3D4C63; }
        /* The auto-generated disclaimer: a very small line under the notes. */
        .pit-auto { font-size: 10px; color: #94A3B8; font-style: italic; display: flex; align-items: center; gap: 3px; margin-top: 2px; line-height: 1.3; max-width: 260px; }
        .pit-legend { display: flex; flex-wrap: wrap; gap: 8px; margin: 6px 0 10px; font-size: 12px; }
        .pit-legend span { padding: 3px 10px; border-radius: 6px; border: 1px solid rgba(0,0,0,0.06); }
        .pit-verified { color: #15803D; font-weight: 600; white-space: nowrap; }
        .pit-unverified { color: #B45309; font-weight: 600; white-space: nowrap; }
        .pit-verify { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 600;
          white-space: nowrap; cursor: pointer; margin: 0; }
        .pit-verify input { width: 15px; height: 15px; cursor: pointer; accent-color: #0F6E56; }
        .pit-verify.on { color: #0F6E56; }
        .pit-verify.off { color: #854F0B; }
        .pit-verify-print.screen-hidden { display: none; }
        .pit-table td, .pit-table th { vertical-align: top; }
        .pit-table tbody tr:nth-child(even) { background: rgba(247,249,252,0.6); }
        .pit-details { min-width: 240px; }
        .pit-notes { min-width: 140px; }
        .pit-section { break-inside: avoid; }
        .pit-empty { border: 1px solid #EDF1F7; border-top: none; border-radius: 0 0 8px 8px; background: #FBFCFE;
          padding: 10px 14px; font-size: 12.5px; font-style: italic; color: #94A3B8; }
        .pit-money { font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
        .pit-files { display: flex; flex-wrap: wrap; gap: 4px; }
        .pit-files a { font-size: 11.5px; color: #1D4ED8; display: inline-flex; align-items: center; gap: 4px;
          max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          background: #EFF6FF; border: 1px solid #DBEAFE; border-radius: 999px; padding: 2px 9px; text-decoration: none; }
        .pit-files a:hover { background: #DBEAFE; }
        .pit-title { font-size: 15px; font-weight: 800; color: #0F1F35; margin: 2px 0 10px; letter-spacing: -0.01em; }
        .pit-gen { font-weight: 500; font-size: 12px; color: #64748B; }
        .pit-counts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 18px; }
        .pit-count { border-radius: 8px; margin-top: 0; justify-content: space-between; }
        @media (max-width: 560px) { .pit-counts { grid-template-columns: 1fr; } }

        /* Print: drop the chrome, keep the data, never split a category across pages.
           The verification CHECKBOX is an action, not a fact — paper gets the text twin. */
        @media print {
          @page { size: landscape; margin: 10mm; }
          .pit-toolbar, .pit-legend { display: none !important; }
          .pit-section { break-inside: avoid; page-break-inside: avoid; }
          .pit-cat { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pit-table { font-size: 10.5px; }
          .pit-details, .pit-notes { max-width: none; }
          .pit-files a { color: #0F1F35; text-decoration: underline; background: none; border: none; padding: 0; }
          a[href]:after { content: ""; }
          .pit-verify { display: none !important; }
          .pit-verify-print.screen-hidden { display: inline !important; }
        }
      `}</style>

      <div className="pit-toolbar">
        <div className="pit-seg" role="tablist" aria-label="Report scope">
          <button type="button" className={mode === "month" ? "on" : ""} onClick={() => setMode("month")}>Month</button>
          <button type="button" className={mode === "current" ? "on" : ""} onClick={() => setMode("current")}>Current status</button>
        </div>
        {mode === "month" && (
          <Input type="month" bsSize="sm" style={{ width: 170 }} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        )}
        <div className="pit-seg" role="tablist" aria-label="Verification filter">
          <button type="button" className={unverifiedOnly ? "" : "on"} onClick={() => setUnverifiedOnly(false)}>All</button>
          <button type="button" className={unverifiedOnly ? "on" : ""} onClick={() => setUnverifiedOnly(true)}>Not verified</button>
        </div>
        <div className="pit-seg ms-2" role="tablist" aria-label="Sheet">
          <button type="button" className={view === "updates" ? "on" : ""} onClick={() => setView("updates")}>Sheet 1 · PAD updates</button>
          <button type="button" className={view === "units" ? "on" : ""} onClick={() => setView("units")}>Sheet 2 · All units</button>
        </div>
        <Button size="sm" color="light" className="ms-auto" onClick={() => window.print()} disabled={!report}>
          <FeatherIcon icon="printer" size={14} /> Print
        </Button>
        <Button size="sm" color="success" onClick={handleExport} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={13} className="me-1" /> Download PAD Excel</>}
        </Button>
        {/* Month mode only: the notice is about one month's work being finished. */}
        {mode === "month" && (
          <Button size="sm" color="primary" onClick={handleNotifyAccounting} disabled={notifying || loading || !mastersheetId}>
            {notifying ? <Spinner size="sm" /> : <><FeatherIcon icon="send" size={13} className="me-1" /> Notify Accounting</>}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center p-4"><Spinner color="primary" /> Loading PAD report…</div>
      ) : error ? (
        <p className="text-danger p-3">{error}</p>
      ) : !report ? (
        <p className="text-muted p-3">No data available.</p>
      ) : (
        <>
          {/* The heading the workbook carries: scope, property, date. */}
          <div className="pit-title">
            PAD Changes · {mode === "month" ? formatMonthLabel(effectiveMonth) : "Current status"}{report.unverifiedOnly ? " · Not verified only" : ""} · {[report.property_name, report.building_name].filter(Boolean).join(" · ")}
            <span className="pit-gen"> (Generated on {String(report.generatedAt || "").slice(0, 10)})</span>
          </div>

          {view === "updates" ? (
            <div>
              {updateCount === 0 && report.carryover.length === 0 && (report.delayed_carryover || []).length === 0 && (report.split_carryover || []).length === 0 && (report.resumes || []).length === 0 ? (
                <p className="text-muted p-3 mb-0">
                  {mode === "month" ? `No PAD changes recorded for ${formatMonthLabel(effectiveMonth)}.` : "No PAD information recorded for this property yet."}
                  {t.none + t.documentOnly > 0 ? ` ${t.none + t.documentOnly} occupied unit${t.none + t.documentOnly === 1 ? "" : "s"} still need PAD info — see Sheet 2.` : ""}
                </p>
              ) : null}
              {[
                ...report.updates,
                ...((report.resumes || []).length ? [{ category: "RESUME", label: "PAD resumes this month — restart the pull", rows: report.resumes }] : []),
                ...((report.delayed_carryover || []).length ? [{ category: "DELAY_CARRYOVER", label: "Ongoing delayed pulls — pull on the delayed date", rows: report.delayed_carryover }] : []),
                ...((report.split_carryover || []).length ? [{ category: "SPLIT_CARRYOVER", label: "Ongoing split pulls — pull each amount on its date", rows: report.split_carryover }] : []),
                ...(report.carryover.length ? [{ category: "CARRYOVER", label: "Ongoing temporary stops — do not pull", rows: report.carryover }] : []),
                // Vacant units close the sections, as a full section like the categories:
                // no pull happens here, who is arriving, and how the last PAD ended.
                {
                  category: "VACANT",
                  label: "Vacant units",
                  // With no Details column, the section's substance moves into the columns
                  // that exist: who is arriving sits where a resident would, and how the
                  // last PAD ended sits in Notes — mirroring the Excel export.
                  rows: (report.vacant || []).map((v) => ({
                    unit_no: v.unit_no,
                    tenant: v.incoming || "— vacant —",
                    effective_date: v.last_pad_date,
                    rent: null,
                    notes: v.last_pad ? `Last PAD: ${v.last_pad}${v.last_tenant ? ` (${v.last_tenant})` : ""}` : "",
                    attachments: [],
                    verified: false,
                    pad_id: "",
                  })),
                },
              ].map((g) => {
                const meta =
                  g.category === "CARRYOVER" ? { color: "#546E7A", icon: "pause" }
                  : g.category === "DELAY_CARRYOVER" ? { color: categoryMeta("DELAY").color, icon: "clock" }
                  : g.category === "SPLIT_CARRYOVER" ? { color: categoryMeta("SPLIT").color, icon: "layers" }
                  : g.category === "RESUME" ? { color: "#0F766E", icon: "play" }
                  : g.category === "VACANT" ? { color: "#5F6B7A", icon: "home" }
                  : categoryMeta(g.category);
                return (
                  <div key={g.category} className="pit-section">
                    <div className="pit-cat" style={{ background: meta.color }}>
                      <FeatherIcon icon={meta.icon} size={14} /> {g.label} <span className="n">{g.rows.length}</span>
                    </div>
                    {/* Every category renders, empty ones included: "nothing was delayed" and
                        "the Delay section is missing" must not look the same. */}
                    {g.rows.length === 0 ? (
                      <div className="pit-empty">{g.category === "VACANT" ? "No vacant units." : mode === "month" ? "No changes this month." : "Nothing recorded."}</div>
                    ) : (
                      <Table hover responsive size="sm" className="pit-table mb-0" style={{ border: "1px solid #E4E9F2", borderTop: "none" }}>
                        <thead className="table-light">
                          <tr>
                            {/* Details left the report at the owner's direction (2026-08-28):
                                the section bar, dates and notes carry what accounting needs. */}
                            <th style={{ width: 70 }}>Unit</th>
                            <th>Resident</th>
                            <th style={{ width: 110 }}>Effective</th>
                            <th className="text-end" style={{ width: 110 }}>Total Rent</th>
                            <th>Notes</th>
                            <th style={{ width: 160 }}>Attachments</th>
                            <th style={{ width: 150 }}>Verified</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r.pad_id || `${r.unit_no}-${r.effective_date}`}>
                              <td><strong>{r.unit_no}</strong></td>
                              <td>{r.tenant || "—"}</td>
                              <td>{r.effective_date || "—"}</td>
                              <td className="text-end pit-money">{r.rent != null ? money(r.rent) : "—"}</td>
                              <td>
                                <div className="pit-notes">{r.notes || "—"}</div>
                                {r.auto_note ? (
                                  <div className="pit-auto">
                                    <FeatherIcon icon="zap" size={9} /> {r.auto_note}
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                {(r.attachments || []).length === 0 ? (
                                  <span className="text-muted">—</span>
                                ) : (
                                  <div className="pit-files">
                                    {r.attachments.map((a, i) => (
                                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" title={a.filename}>
                                        <FeatherIcon icon="paperclip" size={11} /> {a.filename}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>
                                {/* The live checkbox for people who can sign off; text for
                                    everyone else, and always text on paper. */}
                                {canVerify && r.pad_id ? (
                                  <label className={`pit-verify ${r.verified ? "on" : "off"}`} title={r.verified ? `Verified${r.verified_by ? ` by ${r.verified_by}` : ""}` : "Not yet verified by accounting"}>
                                    <input
                                      type="checkbox"
                                      checked={!!r.verified}
                                      disabled={verifyBusy === r.pad_id}
                                      onChange={(ev) => handleVerify(r, ev.target.checked)}
                                    />
                                    {r.verified ? "Verified" : "Not verified"}
                                  </label>
                                ) : null}
                                {/* A vacant row has no entry behind it — nothing to verify. */}
                                <span className={`pit-verify-print ${r.verified ? "pit-verified" : "pit-unverified"}${canVerify && r.pad_id ? " screen-hidden" : ""}`}>
                                  {!r.pad_id ? "—" : r.verified ? `Yes${r.verified_by ? ` — ${r.verified_by}` : ""}` : "Not yet"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                );
              })}
              {/* The suite counts close Sheet 1 in the same bars the categories use; they moved
                  here from the top of the mastersheet. */}
              <div className="pit-counts">
                {[
                  ["Suites", t.unitsTotal, "#1F3864"],
                  ["Occupied", t.occupied, categoryMeta("NEW").color],
                  ["Vacant", t.vacant, categoryMeta("STOP").color],
                ].map(([label, n, color]) => (
                  <div key={label} className="pit-cat pit-count" style={{ background: color }}>
                    {label} <span className="n">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (

            <div>
              <div className="pit-legend">
                <span style={{ background: SOURCE_META.DATA.bg }}>PAD data in Rentdigicare</span>
                <span style={{ background: SOURCE_META.DOCUMENT.bg }}>PAD document only — no data</span>
                <span style={{ background: SOURCE_META.NONE.bg }}>Nothing available</span>
                <span style={{ background: "#E7E6E6" }}>Vacant</span>
                <span style={{ background: "#DDEBF7" }}>Marked N/A (not on PAD)</span>
              </div>
              <Table hover responsive size="sm" className="pit-table">
                <thead className="table-light">
                  <tr>
                    <th>Unit</th><th>Resident</th><th>Occupancy</th><th>PAD status</th><th>Source</th><th>Category</th><th>Effective</th><th className="text-end">Total Rent</th><th>Pull day</th><th>Details</th><th>Notes</th><th>Verified</th><th>Doc updated</th>
                  </tr>
                </thead>
                <tbody>
                  {report.units.map((u) => {
                    const st = PAD_STATE_META[u.pad_state] || PAD_STATE_META.NO_PAD;
                    return (
                      <tr key={u.unit_no} className={rowClass(u)}>
                        <td><strong>{u.unit_no}</strong></td>
                        <td>{u.tenant || (u.occupancy === "VACANT" ? <em className="text-muted">vacant</em> : "—")}</td>
                        <td>{u.occupancy === "VACANT" ? (u.incoming_month ? `Vacant · prospect ${formatMonthLabel(u.incoming_month)}` : "Vacant") : "Occupied"}</td>
                        <td>{u.doc_na && u.source !== "DATA" ? "Not on PAD (N/A)" : st.label}</td>
                        <td>{u.occupancy === "VACANT" && u.source === "NONE" ? "—" : SOURCE_META[u.source]?.label}</td>
                        <td>{u.category_label || "—"}</td>
                        <td>{u.effective_date || "—"}</td>
                        <td className="text-end pit-money">{u.rent != null ? money(u.rent) : "—"}</td>
                        <td>{u.pull_day ?? "—"}</td>
                        <td><div className="pit-details">{u.details}</div></td>
                        <td><div className="pit-notes">{u.notes || "—"}</div></td>
                        <td>{u.source === "DATA" ? (u.verified ? <span className="pit-verified">Yes{u.verified_by ? ` — ${u.verified_by}` : ""}</span> : <span className="pit-unverified">Not yet</span>) : "—"}</td>
                        <td>{u.doc_last_updated || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { PadInfoTab };
